'use client'
import * as React from 'react'
import { localStore } from 'utils/storage'

/* eslint-disable @typescript-eslint/no-explicit-any */
function dispatchStorageEvent(key: string, newValue: any) {
  if (key) {
    window.dispatchEvent(new StorageEvent('storage', { key, newValue }))
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function setLocalStorageItem(key: string, value: any) {
  const stringifiedValue = JSON.stringify(value)
  localStore.setString(key, stringifiedValue)
  dispatchStorageEvent(key, stringifiedValue)
}

const removeLocalStorageItem = (key: string) => {
  localStore.remove(key)
  dispatchStorageEvent(key, null)
}

const getLocalStorageItem = (key: string) => {
  return localStore.getString(key)
}

const useLocalStorageSubscribe = (callback: () => void) => {
  window.addEventListener('storage', callback)
  return () => window.removeEventListener('storage', callback)
}

const getLocalStorageServerSnapshot = () => {
  return null
}

export function useLocalStorage<T>(
  key: string,
  initialValue?: T,
  enabled = true,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const getSnapshot = () => getLocalStorageItem(key)

  const store = React.useSyncExternalStore(
    useLocalStorageSubscribe,
    getSnapshot,
    getLocalStorageServerSnapshot,
  )

  const setState = React.useCallback(
    (v: React.SetStateAction<any>) => {
      try {
        // oxlint-disable-next-line no-nested-ternary
        const nextState = typeof v === 'function' ? (store ? v(JSON.parse(store)) : null) : v

        if (nextState === undefined || nextState === null) {
          removeLocalStorageItem(key)
        } else {
          setLocalStorageItem(key, nextState)
        }
        // oxlint-disable-next-line unicorn/catch-error-name
      } catch (e) {}
    },
    [key, store],
  )

  React.useEffect(() => {
    if (getLocalStorageItem(key) === null && typeof initialValue !== 'undefined' && enabled) {
      setLocalStorageItem(key, initialValue)
    }
  }, [key, initialValue, enabled])

  return [store ? JSON.parse(store) : initialValue, setState]
}
